import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import React, { useEffect, useState } from 'react';
import { AppContainer } from './components/AppContainer/AppContainer';
import { AnimatedBeamGraphLogin } from './components/Effects/AnimatedBeamGraph';
import { Login } from './components/Login/Login';
import { AuthHelper } from './libs/auth/AuthHelper';
import { useChat } from './libs/hooks';
import { useAppDispatch, useAppSelector } from './redux/app/hooks';
import { RootState } from './redux/app/store';
import { setActiveUserInfo, setServiceInfo } from './redux/features/app/appSlice';
import { FeatureKeys } from './redux/features/app/AppState';
import { ThemeProvider } from './theme/ThemeContext';

export enum AppState {
    Login,
    ErrorLoadingChats,
    ErrorLoadingUserInfo,
    LoadingChats,
    Chat,
    SigningOut,
}

export const App: React.FC = () => {
    const [appState, setAppState] = useState<AppState>(AppState.Login);
    const { features, isHideChatListOnLoadEnabled } = useAppSelector((state: RootState) => state.app);
    const { instance, inProgress } = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const chat = useChat();
    const dispatch = useAppDispatch();

    const hasMessageIdInQueryString = (): boolean => {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.has('messageId');
    };

    const handleResetAndRefresh = () => {
        localStorage.clear();
        setTimeout(() => window.location.reload(), 100);
    };

    useEffect(() => {
        if (isAuthenticated && appState === AppState.Login) {
            const account = instance.getActiveAccount();

            if (!account) {
                setAppState(AppState.ErrorLoadingUserInfo);
            } else {
                dispatch(
                    setActiveUserInfo({
                        id: `${account.localAccountId}.${account.tenantId}`,
                        email: account.username, // username is the email address
                        username: account.name ?? account.username,
                    }),
                );

                setAppState(AppState.LoadingChats);
            }
        }

        if ((isAuthenticated || !AuthHelper.isAuthAAD()) && appState === AppState.LoadingChats) {
            void Promise.all([
                features[FeatureKeys.AutoCreateChat].enabled && !hasMessageIdInQueryString()
                    ? chat
                          .createChat(true)
                          .then(() => {
                              setAppState(AppState.Chat);
                          })
                          .catch(() => {
                              setAppState(AppState.ErrorLoadingChats);
                          })
                    : chat
                          .loadChats({}, false, 1, 1)
                          .then(() => {
                              setAppState(AppState.Chat);
                          })
                          .catch(() => {
                              setAppState(AppState.ErrorLoadingChats);
                          }),
                // Load user permissions
                chat.getUserPermissions(),

                // Load service information
                chat.getServiceInfo().then((serviceInfo) => {
                    if (serviceInfo) {
                        dispatch(setServiceInfo(serviceInfo));
                    }
                }),
            ]);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [instance, inProgress, isAuthenticated, appState]);

    //const isDarkModeInherit = document.querySelector('html[data-theme="dark"]') !== null;

    return (
        <ThemeProvider>
            <div className="m-0 p-0 h-dvh text-sm text-slate-700 dark:text-slate-200">
                {appState === AppState.Login && <Login />}
                {appState === AppState.ErrorLoadingUserInfo && (
                    <>
                        {handleResetAndRefresh()}
                        <p>One moment...</p>
                    </>
                )}
                {appState === AppState.ErrorLoadingChats && <p>Error loading chats</p>}
                {appState === AppState.LoadingChats && (
                    <div>
                        <AnimatedBeamGraphLogin className="hidden sm:flex absolute z-[200] top-20 mt-10 left-1/2 transform -translate-x-1/2" />
                    </div>
                )}
                {appState === AppState.Chat && (
                    <AppContainer setAppState={setAppState} isChatListHidden={isHideChatListOnLoadEnabled || false} />
                )}
            </div>
        </ThemeProvider>
    );
};
